import {
  Box,
  BoxProps,
  Flex,
  HelpLinkIFrame,
  LinkAnchor,
  mdBumps,
  smBumps,
  Typo,
  useModal,
  xxlBumps,
} from '@wrisk/ui-components'
import React, { useCallback } from 'react'

import { CoverOption, Quote } from '../../../../domain'
import {
  TKey,
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import {
  getOptionalCoverLink,
  getOptionalCoversContentBaseUrl,
  useConfig,
} from '../../../../state/configuration'
import { useProduct } from '../../../product/productContext'
import { AddOnButtons } from './components/Buttons'
import { AddOnHeader } from './components/Header'

interface AddOnSelectorProps extends BoxProps {
  quote: Quote
  coverOption: CoverOption
  onChange: (cover: CoverOption) => void
  tKey: TKeyBuilder
}

const tCovers = TKey('covers')

export const AddOnBoolean: React.FC<AddOnSelectorProps> = ({
  quote,
  coverOption,
  onChange,
  tKey,
  ...props
}) => {
  const { t } = useWriskTranslation()
  const modal = useModal()

  const { scheme } = useProduct()

  const contentBaseUrl = useConfig(getOptionalCoversContentBaseUrl(scheme.schemeCode))
  const insurer = coverOption.insurer
  const link =
    insurer &&
    getOptionalCoverLink(contentBaseUrl, quote.wordingVersion, {
      ...coverOption,
      insurer,
    })

  const id = `section-add-on-${coverOption.coverGroup}`

  const onChangeCallback = useCallback(
    (active: boolean | undefined) =>
      coverOption.available && onChange({ ...coverOption, active }),
    [onChange, coverOption],
  )

  const onLinkMoreClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault()
      modal.show({
        content: <HelpLinkIFrame src={link} />,
        variant: 'iframe',
      })
    },
    [modal, link],
  )

  return coverOption.available ? (
    <Box data-testid={coverOption.coverGroup} mb={xxlBumps} width={1} {...props}>
      <Flex flexDirection='column' justifyContent='flex-start' mb={2} variant='raised'>
        <AddOnHeader
          tKey={tKey}
          amount={coverOption.premium}
          disabled={false}
          text={t<string>(tKey('add-ons', coverOption.coverGroup, 'header'))}
        />
        <Box px={mdBumps} pb={mdBumps} width={1}>
          <Typo mb={smBumps}>{t(tCovers(coverOption.coverGroup, 'description'))}</Typo>
          <LinkAnchor typoSize='sm' onClick={onLinkMoreClick} variant='standalone'>
            {t(tKey('add-ons', coverOption.coverGroup, 'actions.more'))}
          </LinkAnchor>
        </Box>
      </Flex>
      <AddOnButtons
        selected={coverOption.active}
        prefixForIds={id}
        titleYes={t(tKey('add-ons', coverOption.coverGroup, 'actions.yes'))}
        titleNo={t(tKey('add-ons', coverOption.coverGroup, 'actions.no'))}
        onSelect={onChangeCallback}
      />
    </Box>
  ) : (
    <Box mb={xxlBumps} width={1} {...props}>
      <Flex flexDirection='column' justifyContent='flex-start' mb={2} variant='raised'>
        <AddOnHeader
          tKey={tKey}
          disabled={true}
          text={t<string>(tCovers(coverOption.coverGroup, 'name'))}
        />
        <Box px={mdBumps} pb={mdBumps} width={1}>
          <Typo>{t(tCovers(coverOption.coverGroup, 'unavailable', 'description'))}</Typo>
        </Box>
      </Flex>
    </Box>
  )
}
